import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const PerformanceChart = ({ data, collections }) => {
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('6M');

  const timeRangeOptions = [
    { label: '1M', value: '1M' },
    { label: '3M', value: '3M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: 'All', value: 'All' }
  ];

  const chartTypes = [
    { type: 'line', icon: 'TrendingUp', label: 'Performance' },
    { type: 'bar', icon: 'BarChart3', label: 'Categories' },
    { type: 'pie', icon: 'PieChart', label: 'Distribution' }
  ];

  // Generate demo data with positive growth for mike@poetsroad.com
  const generateDemoData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const baseValues = {
      'watch': 27300, // Vintage watches starting value
      'comics': 90000, // Rare comics starting value  
      'first-editions': 20300 // First editions starting value
    };

    return months?.map((month, index) => {
      const watchGrowth = 1 + (index * 0.08); // 8% monthly growth
      const comicsGrowth = 1 + (index * 0.06); // 6% monthly growth
      const booksGrowth = 1 + (index * 0.07); // 7% monthly growth
      
      return {
        month,
        value: Math.round(
          (baseValues?.watch * watchGrowth) +
          (baseValues?.comics * comicsGrowth) +
          (baseValues?.['first-editions'] * booksGrowth)
        ),
        watches: Math.round(baseValues?.watch * watchGrowth),
        comics: Math.round(baseValues?.comics * comicsGrowth),
        books: Math.round(baseValues?.['first-editions'] * booksGrowth)
      };
    });
  };

  // Use demo data if no real data provided
  const chartData = data?.length > 0 ? data : generateDemoData();

  const pieData = collections?.length > 0 ? collections?.map((collection, index) => ({
    name: collection?.name,
    value: collection?.value || collection?.total_value,
    color: ['#D4AF37', '#FF6B35', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6']?.[index % 6]
  })) : [
    { name: 'Vintage Watches', value: 47300, color: '#D4AF37' },
    { name: 'Rare Comics', value: 102000, color: '#FF6B35' },
    { name: 'First Editions', value: 31300, color: '#10B981' }
  ];

  const categoryData = collections?.length > 0 ? collections?.map(collection => ({
    name: collection?.name?.split(' ')?.[0] || collection?.name,
    value: collection?.value || collection?.total_value,
    items: collection?.items || collection?.item_count,
    change: collection?.change || '+12.5%'
  })) : [
    { name: 'Watches', value: 47300, items: 3, change: '+18.5%' },
    { name: 'Comics', value: 102000, items: 3, change: '+15.2%' },
    { name: 'Books', value: 31300, items: 3, change: '+16.8%' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-premium-lg">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-sm text-popover-foreground">
                {entry?.name}: ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="month" 
                stroke="#A0A3B1" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#A0A3B1" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#D4AF37" 
                strokeWidth={3}
                dot={{ fill: '#D4AF37', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#D4AF37', strokeWidth: 2 }}
              />
              {/* Additional lines for individual collections */}
              <Line 
                type="monotone" 
                dataKey="watches" 
                stroke="#FF6B35" 
                strokeWidth={2}
                dot={false}
                strokeDasharray="5 5"
              />
              <Line 
                type="monotone" 
                dataKey="comics" 
                stroke="#10B981" 
                strokeWidth={2}
                dot={false}
                strokeDasharray="5 5"
              />
              <Line 
                type="monotone" 
                dataKey="books" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                dot={false}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="name" 
                stroke="#A0A3B1" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#A0A3B1" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill="#D4AF37"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(2)}%`}
                labelLine={false}
              >
                {pieData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  // Calculate current analytics values
  const totalValue = chartData?.length > 0 ? chartData?.[chartData?.length - 1]?.value : 180600;
  const startValue = chartData?.length > 0 ? chartData?.[0]?.value : 137600;
  const growthPercentage = ((totalValue - startValue) / startValue * 100)?.toFixed(2);
  const unrealizedGains = totalValue - startValue;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4 sm:mb-0">Portfolio Analytics</h3>
        
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Chart Type Selector */}
          <div className="flex bg-muted/30 rounded-lg p-1">
            {chartTypes?.map((type) => (
              <button
                key={type?.type}
                onClick={() => setChartType(type?.type)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  chartType === type?.type
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-card-foreground'
                }`}
              >
                <Icon name={type?.icon} size={16} />
                <span className="hidden sm:inline">{type?.label}</span>
              </button>
            ))}
          </div>

          {/* Time Range Dropdown - Only show for line chart */}
          {chartType === 'line' && (
            <div className="w-32">
              <Select
                value={timeRange}
                onChange={setTimeRange}
                options={timeRangeOptions}
                placeholder="Duration"
                className="text-sm"
              />
            </div>
          )}
        </div>
      </div>
      
      <div className="w-full h-80">
        {renderChart()}
      </div>
      
      {/* Chart Insights with Real Data */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold text-success">+{growthPercentage}%</div>
          <div className="text-sm text-muted-foreground">12M Performance</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">${totalValue?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Current Value</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-warning">${unrealizedGains?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Unrealized Gains</div>
        </div>
      </div>

      {/* Collection Legend for Line Chart */}
      {chartType === 'line' && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-4 justify-center text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span className="text-muted-foreground">Total Portfolio</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-1 bg-orange-500 rounded-full"></div>
              <span className="text-muted-foreground">Vintage Watches</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-1 bg-green-500 rounded-full"></div>
              <span className="text-muted-foreground">Rare Comics</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-1 bg-purple-500 rounded-full"></div>
              <span className="text-muted-foreground">First Editions</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceChart;